import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-800 mb-8 inline-block">&larr; Back to Jubly</Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: March 25, 2026</p>

        <div className="prose prose-gray max-w-none text-[15px] leading-relaxed">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing or using Jubly (the "Service"), operated by Hapi.vc, LLC ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            Jubly is a unified communication platform that provides email, calendar, voice calling, SMS messaging, video meetings, and document collaboration services. The Service includes:
          </p>
          <ul>
            <li>Email hosting with @jubly.me addresses</li>
            <li>Calendar with event scheduling and sharing</li>
            <li>Voice calling via browser (WebRTC) with phone numbers</li>
            <li>SMS/MMS text messaging</li>
            <li>Video conferencing with screen sharing and recording</li>
            <li>Collaborative document editing</li>
          </ul>

          <h2>3. Account Registration</h2>
          <p>
            To use the Service, you must create an account with a valid recovery email address. You are responsible for maintaining the security of your account credentials. You must be at least 13 years old to create an account.
          </p>

          <h2>4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for spam, unsolicited messages, or bulk messaging</li>
            <li>Send fraudulent, deceptive, or illegal content</li>
            <li>Harass, threaten, or abuse other users or third parties</li>
            <li>Impersonate any person or entity</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Use the Service to violate any applicable law or regulation</li>
            <li>Transmit malware, viruses, or other harmful code</li>
            <li>Use automated systems to access the Service without permission</li>
          </ul>

          <h2>5. SMS/MMS Messaging Terms</h2>
          <p>By using Jubly's messaging features, you agree to:</p>
          <ul>
            <li>Only send messages to recipients who have consented to receive them</li>
            <li>Comply with the Telephone Consumer Protection Act (TCPA) and all applicable messaging regulations</li>
            <li>Honor opt-out requests immediately (STOP keyword)</li>
            <li>Not send messages for illegal purposes</li>
            <li>Not exceed reasonable messaging volume limits</li>
          </ul>
          <p>
            Message and data rates may apply. Message frequency varies. Reply STOP to opt out. Reply HELP for help. See our <Link to="/privacy" className="text-blue-600">Privacy Policy</Link> for data handling details.
          </p>

          <h2>6. Voice Calling Terms</h2>
          <p>
            Voice services are provided through Telnyx. You agree to use voice calling in compliance with all applicable laws, including regulations regarding call recording, caller ID, and robocalling. You are responsible for informing call participants when recording is enabled.
          </p>

          <h2>7. Phone Numbers</h2>
          <p>
            Phone numbers provisioned through the Service are leased, not owned. We may reclaim or reassign numbers for inactive accounts. You may port your number to another provider subject to applicable regulations.
          </p>

          <h2>8. Intellectual Property</h2>
          <p>
            The Service, including its design, code, and branding, is owned by Hapi.vc, LLC. Content you create or upload through the Service remains yours. By using the Service, you grant us a limited license to store, process, and transmit your content as necessary to provide the Service.
          </p>

          <h2>9. Privacy</h2>
          <p>
            Your use of the Service is also governed by our <Link to="/privacy" className="text-blue-600">Privacy Policy</Link>, which describes how we collect, use, and protect your information.
          </p>

          <h2>10. Service Availability</h2>
          <p>
            We strive to maintain high availability but do not guarantee uninterrupted access. The Service may be temporarily unavailable for maintenance, updates, or due to factors beyond our control. We are not liable for any loss resulting from Service interruptions.
          </p>

          <h2>11. Fees and Payment</h2>
          <p>
            Basic Jubly accounts are free. Premium features, additional phone numbers, or increased usage may be subject to fees. We will notify you before charging for any paid features. Voice calling and SMS usage may incur per-minute or per-message charges.
          </p>

          <h2>12. Termination</h2>
          <p>
            You may close your account at any time. We may suspend or terminate your account if you violate these Terms. Upon termination, your data will be deleted in accordance with our data retention policies.
          </p>

          <h2>13. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED.
          </p>

          <h2>14. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, HAPI.VC, LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
          </p>

          <h2>15. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Indiana, United States, without regard to conflict of law principles.
          </p>

          <h2>16. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the updated Terms.
          </p>

          <h2>17. Contact</h2>
          <p>
            Hapi.vc, LLC<br />
            110 Westwood Rd<br />
            Goshen, Indiana 46526<br />
            Email: <a href="mailto:support@jubly.net">support@jubly.net</a><br />
            Phone: +1 (574) 216-2626
          </p>
        </div>
      </div>
    </div>
  );
}
